import { apiService } from './api.service';

export type ChangeItemKind =
  | 'module'
  | 'addon'
  | 'feature'
  | 'quota'
  | 'plan_template';
export type ChangeItemAction = 'add' | 'remove' | 'update_quantity';

export interface SubmitChangeItem {
  itemKind: ChangeItemKind;
  action: ChangeItemAction;
  targetCode: string;
  quantity?: number | null;
  notes?: string | null;
}

export interface SubmitChangeRequest {
  businessId: string;
  subscriptionId?: string | null;
  customerNote?: string | null;
  summary?: string | null;
  items: SubmitChangeItem[];
}

export type ChangeRequestStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'applied'
  | 'cancelled'
  | 'pending_compliance';

export interface CustomerChangeRequest {
  id: string;
  businessId: string;
  requestStatus: ChangeRequestStatus;
  scheduledEffectiveAt: string | null;
  appliedAt: string | null;
  cancelledAt: string | null;
  cancelledByType: 'customer' | 'admin' | null;
  customerNote: string | null;
  summary: string | null;
  createdAt: string;
  /**
   * Free-form metadata bag. Phase 6 (compliance) writes
   * `complianceBreaches: [{ resource, current, limit }]` here when
   * the scheduler blocks the apply.
   */
  metadata: Record<string, unknown> | null;
}

export interface CustomerChangeItem {
  id: string;
  itemKind: ChangeItemKind;
  action: ChangeItemAction;
  targetCode: string;
  quantity: number | null;
}

export interface CustomerChangeRequestWithItems {
  request: CustomerChangeRequest;
  items: CustomerChangeItem[];
}

export interface PreviewRemovalImpact {
  itemKind: ChangeItemKind;
  targetCode: string;
  hasUnpaidProrationInvoice: boolean;
  blocked?: { reason: string; code: string };
  unpaidInvoiceId?: string;
  unpaidInvoiceNumber?: string;
  originalInvoiceTotal?: string;
  newInvoiceTotal?: string;
  creditAmount?: string;
  daysUsed?: number;
  daysCoverage?: number;
  /**
   * Cancel-at-period-end summary. Present when the subscription has
   * an active `currentPeriodEnd` and the catalog price for the item
   * resolves. `delta` is negative — the amount the customer WON'T
   * be billed on the next recurring invoice.
   */
  scheduledRemoval?: {
    effectiveAt: string;
    monthlyPrice: number;
    delta: number;
    currency: string;
    /**
     * True when the recurring bill for this item comes from a
     * `subscription_override` (sadmin-granted sponsorship), not
     * the catalog list price. FE warns the operator they're
     * forfeiting the discount at period end.
     */
    isCourtesyPriced?: boolean;
    /** True when the override is `isPerpetual=true` — never-expires cortesía. */
    isPerpetual?: boolean;
  };
}

export const subscriptionChangesService = {
  /**
   * Wraps `POST /api/subscription-changes/me`. The backend handles
   * the rest of the lifecycle (auto-approve + auto-apply for
   * additive baskets, schedule-at-period-end for reductive ones).
   *
   * Used from ModulePaywall + AvailableAddonsSection so customers
   * never have to bounce through a separate `/account/subscription-
   * changes` page just to pick a SKU they already chose.
   */
  submit: (payload: SubmitChangeRequest): Promise<unknown> =>
    apiService.getInstance().post('/subscription-changes/me', payload),

  /**
   * Wraps `GET /api/subscription-changes/me`. Returns the business's
   * own change requests across every status, with the basket items
   * inline. The /account page filters to `approved` + non-null
   * `scheduledEffectiveAt` to surface scheduled downgrades AND to
   * badge plan cards with the upcoming plan_template change.
   */
  list: (): Promise<CustomerChangeRequestWithItems[]> =>
    apiService.getInstance().get('/subscription-changes/me'),

  /**
   * Wraps `GET /api/subscription-changes/me/:id` — used when the
   * /account scheduled downgrade row is expanded to show the items
   * being removed.
   */
  detail: (id: string): Promise<CustomerChangeRequestWithItems> =>
    apiService.getInstance().get(`/subscription-changes/me/${id}`),

  /**
   * Cancels a scheduled downgrade. Backend allows cancel from both
   * `pending` and `approved` states, so this also covers cancelling
   * an as-yet-unapplied scheduled apply.
   */
  cancel: (id: string): Promise<CustomerChangeRequest> =>
    apiService.getInstance().post(`/subscription-changes/me/${id}/cancel`, {}),

  /**
   * Sadmin view of any business's change requests. The customer
   * route coerces the id to the caller's own `businessId`, so sadmin
   * needs this separate path.
   */
  listForBusiness: (
    businessId: string
  ): Promise<CustomerChangeRequestWithItems[]> =>
    apiService
      .getInstance()
      .get(`/sadmin/subscription-changes/business/${businessId}`),

  /**
   * Forces an approved change request to materialize now, ahead of
   * its `scheduledEffectiveAt`. Backend re-runs the plan-compliance
   * check and rejects with CHANGE_REQUEST_COMPLIANCE_BLOCKED when
   * the business still exceeds the target plan's limits.
   */
  applyNow: (id: string): Promise<unknown> =>
    apiService.getInstance().post(`/sadmin/subscription-changes/${id}/apply`),

  /**
   * Customer counterpart of applyNow — force-applies a scheduled
   * downgrade the caller OWNS. Compliance guard still fires backend-
   * side (CHANGE_REQUEST_COMPLIANCE_BLOCKED when the business
   * exceeds the target plan). Used by the "Activar ahora" button on
   * the ScheduledDowngradesSection.
   */
  selfApplyNow: (id: string): Promise<unknown> =>
    apiService.getInstance().post(`/subscription-changes/me/${id}/apply-now`),

  /**
   * Dry-run the financial impact of removing an addon RIGHT NOW.
   * Backend returns blocked-reason (plan-included), unpaid invoice
   * recompute preview (new total + credit + days used), OR a no-op
   * flag when nothing needs adjusting. FE surfaces these in the
   * remove-confirmation modal so the customer sees exactly what
   * happens before clicking Quitar.
   */
  previewRemoval: (
    itemKind: ChangeItemKind,
    targetCode: string
  ): Promise<PreviewRemovalImpact> =>
    apiService.getInstance().post('/subscription-changes/me/preview-removal', {
      itemKind,
      targetCode,
    }),
};
